import { FormEvent, useState } from "react";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import "@/components/PaymentForm/PaymentForm.scss";

interface PaymentFormProps {
  /** Label of the pay button, e.g. "Pay CHF 16,450". */
  submitLabel: string;
  /** Where Stripe sends the guest back after paying (and after 3-D Secure or TWINT redirects). */
  returnUrl: string;
  disabled?: boolean;
}

/** Stripe's Payment Element plus the pay button. Must be rendered inside Stripe's <Elements>. */
function PaymentForm({
  submitLabel,
  returnUrl,
  disabled = false,
}: PaymentFormProps) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [isPaying, setIsPaying] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }

    setError(null);
    setIsPaying(true);

    // On success Stripe navigates to returnUrl, so we only get here when something went wrong.
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
    });

    setError(result.error?.message ?? t("payment.genericError"));
    setIsPaying(false);
  };

  return (
    <form className="payment-form" onSubmit={handleSubmit}>
      <PaymentElement options={{ layout: "tabs" }} />

      {error && (
        <p className="payment-form__error" role="alert">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={!stripe || !elements || isPaying || disabled}
      >
        {isPaying ? t("payment.paying") : submitLabel}
      </button>
    </form>
  );
}

export default PaymentForm;
export type { PaymentFormProps };
